import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete as MuiAutocomplete, Box, CircularProgress, TextField, Typography } from "@mui/material";
import { useTranslation } from "../../../hooks/useTranslation";
import { StateType } from "../../../backend/store";
import { authWorkspaceActions } from "../../../backend/store/reducers/authReducer";
import { fetchPlacePredictions } from "../../../utils/placePredictions";
import { isUsOrUk } from "../../../constants/geoLists";
import { isHebrewText } from "../../../utils/isHebrewText";

interface PlacePrediction {
  place_id: string;
  description: string;
}

interface IProps {
  /** Show the "Default region for manual addresses" heading. Hidden inside the
   *  dialog, which supplies its own title. */
  showLabel?: boolean;
}

/**
 * Manual-address default-region picker — a region-level Google autocomplete
 * (country + state/region only) whose resolved Country + State pre-seed every new
 * manual address. Saved immediately on selection (or clear) via
 * `updateWorkspaceSettings`, exactly like it did inline in WorkspaceSettingsCard.
 *
 * Self-contained (its own predictions/geocoder state + Redux read/dispatch) so it
 * can be reused both on the Settings page and inside ManualRegionDialog.
 */
export const ManualRegionPicker: React.FC<IProps> = ({ showLabel = true }) => {
  const { p } = useTranslation("workspaceSettings");
  const dispatch = useDispatch();
  const workspace = useSelector((state: StateType) => state.authStore.workspace);
  const isSaving = useSelector((state: StateType) => state.authStore.workspaceSettingsSaving);

  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const [manualRegionPredictions, setManualRegionPredictions] = useState<PlacePrediction[]>([]);
  const [selectedManualRegion, setSelectedManualRegion] = useState<PlacePrediction | null>(null);
  const [isResolvingManualRegion, setIsResolvingManualRegion] = useState(false);
  // Set when the user picks a country that routes per-state (US/UK) without drilling down to a
  // specific state. Manual addresses seed their state from this region, and route division/OSRM
  // need a state for those countries, so an incomplete US/UK region is rejected here at the source.
  const [regionError, setRegionError] = useState<string | null>(null);

  // Keep the displayed label in sync with the stored region name.
  useEffect(() => {
    if (workspace?.manual_default_region_name) {
      setSelectedManualRegion({ place_id: "", description: workspace.manual_default_region_name });
    } else {
      setSelectedManualRegion(null);
    }
  }, [workspace?.manual_default_region_name]);

  const handleManualRegionInputChange = (
    _event: React.SyntheticEvent,
    inputValue: string,
    reason: "input" | "reset" | "clear"
  ) => {
    if (reason !== "input") return;
    if (inputValue.length < 2) {
      setManualRegionPredictions([]);
      return;
    }
    fetchPlacePredictions(inputValue, {
      includedPrimaryTypes: ["administrative_area_level_1", "country"],
    })
      .then((preds) => setManualRegionPredictions(preds))
      .catch(() => setManualRegionPredictions([]));
  };

  const handleManualRegionSelect = (_event: React.SyntheticEvent, value: PlacePrediction | null) => {
    setRegionError(null);
    if (!value) {
      // Cleared — wipe the stored default region.
      setSelectedManualRegion(null);
      dispatch(
        authWorkspaceActions.updateWorkspaceSettings({
          manual_default_region_name: null,
          manual_default_state: null,
          manual_default_country: null,
        })
      );
      return;
    }

    if (!geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }

    setIsResolvingManualRegion(true);
    geocoderRef.current.geocode(
      { placeId: value.place_id, language: isHebrewText(value.description) ? "iw" : undefined },
      (results, geoStatus) => {
      setIsResolvingManualRegion(false);
      if (geoStatus !== window.google.maps.GeocoderStatus.OK || !results || results.length === 0) {
        setSelectedManualRegion(null);
        return;
      }
      const result = results[0];
      const getComponent = (type: string): string | null =>
        result.address_components.find((c) => c.types.includes(type))?.long_name ?? null;

      const country = getComponent("country");
      const state = getComponent("administrative_area_level_1");
      // The region MUST resolve to a real country (any country worldwide is fine — ConnectYid has
      // Google backup everywhere). A pick that geocodes to no country would seed a country-less
      // "problematic" region, so reject it and ask the user to pick a real country/region.
      if (!country) {
        setRegionError(p("manual_default_region_country_required"));
        setSelectedManualRegion(null);
        return;
      }
      // A country that routes per-state (US/UK) MUST carry a state — otherwise a manual address
      // seeded from this region would have no state and drop out of state-based route division.
      // Reject the incomplete pick and ask the user to choose a specific state/region.
      if (isUsOrUk(country) && !state) {
        setRegionError(p("manual_default_region_state_required"));
        setSelectedManualRegion(null);
        return;
      }
      setSelectedManualRegion({ place_id: value.place_id, description: result.formatted_address });
      dispatch(
        authWorkspaceActions.updateWorkspaceSettings({
          manual_default_region_name: result.formatted_address,
          manual_default_state: state,
          manual_default_country: country,
        })
      );
      }
    );
  };

  return (
    <Box>
      {showLabel && (
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          {p("manual_default_region_label")}
        </Typography>
      )}
      <MuiAutocomplete<PlacePrediction>
        options={manualRegionPredictions}
        value={selectedManualRegion}
        onChange={handleManualRegionSelect}
        onInputChange={handleManualRegionInputChange}
        getOptionLabel={(option) => option.description}
        isOptionEqualToValue={(option, value) => option.description === value.description}
        filterOptions={(x) => x}
        disabled={isResolvingManualRegion || isSaving}
        // The options dropdown portals to <body> with a default z-index (1300); when this
        // picker is shown inside ManualRegionDialog (z-index 10002, itself above the
        // address-editor modal) the list would render BEHIND the dialog and look empty.
        // Lift it above the dialog via `sx` (NOT `style` — `style` would replace MUI's
        // internal width style and shrink the dropdown). Harmless on the Settings page.
        componentsProps={{ popper: { sx: { zIndex: 10010 } } }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={p("manual_default_region_placeholder")}
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isResolvingManualRegion ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {regionError && (
        <Typography variant="caption" sx={{ color: "error.main", mt: 0.5, display: "block" }}>
          {regionError}
        </Typography>
      )}
    </Box>
  );
};
